import { Box, Collapse, Stack } from "@mui/material";
import {
  Button,
  Chip,
  type ChipStatus,
  Icon,
  Typography,
} from "@platform/design-system";
import { useState } from "react";

import { useCensusTemplateDownload } from "../hooks/useCensusTemplateDownload";
import {
  type CensusUploadCheckState,
  useCensusUploadCheck,
} from "../hooks/useCensusUploadCheck";
import { readSucceeded, unreadableReason } from "../outcome";
import {
  describeSuggestion,
  type SuggestionText,
} from "../uploadCheck/suggestionText";

import type { CensusCheckResponse } from "@platform/api/bp-api";

interface CensusUploadCheckProps {
  readonly rfpId: number;
  readonly documentId: number;

  /** Open the suggestions without a click, as right after an upload. */
  readonly openSuggestions?: boolean;
}

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * bp-api sends one suggestion per kind, field or column, and set of headings,
 * so together they tell any two apart.
 */
const suggestionKey = ({
  kind,
  field,
  column,
  headers = [],
}: CensusCheckResponse["suggestions"][number]): string =>
  [kind, field ?? column, ...headers].join("|");

const StatusRow = ({
  chipLabel,
  chipColor,
  summary,
  action,
}: {
  readonly chipLabel: string;
  readonly chipColor: ChipStatus;
  readonly summary: string;
  readonly action?: React.ReactNode;
}): React.JSX.Element => (
  <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
    <Chip
      label={chipLabel}
      color={chipColor}
      variant="subtle"
      size="small"
      leadingVisual={false}
      data-testid="census-upload-check-status"
    />
    <Typography variant="body-medium" data-testid="census-upload-check-summary">
      {summary}
    </Typography>
    {action && <Box sx={{ marginLeft: "auto" }}>{action}</Box>}
  </Stack>
);

const SuggestionItem = ({
  suggestion,
}: {
  readonly suggestion: SuggestionText;
}): React.JSX.Element => {
  const [showAllRows, setShowAllRows] = useState(false);
  const hasHiddenRows = suggestion.allDetails.length > 0;
  const details =
    hasHiddenRows && showAllRows ? suggestion.allDetails : suggestion.details;

  return (
    <Stack direction="row" gap={1} data-testid="census-upload-check-suggestion">
      <Icon name="warning" size="sm" color="warning" />
      <Stack gap={0.25}>
        <Typography variant="body-medium">{suggestion.title}</Typography>
        {details.map((detail) => (
          <Typography key={detail} variant="caption" color="textSecondary">
            {detail}
          </Typography>
        ))}
        {hasHiddenRows && (
          <Box>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllRows((shown) => !shown)}
            >
              {showAllRows ? "Hide rows" : "Show all rows"}
            </Button>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

const UnrecognizedHeadings = ({
  headings,
}: {
  readonly headings: readonly string[];
}): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Stack gap={0.5}>
      <Box>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen((open) => !open)}
        >
          {`We didn't recognize ${plural(headings.length, "heading")}`}
        </Button>
      </Box>
      <Collapse in={isOpen}>
        <Typography variant="caption" color="textSecondary">
          {headings.join(", ")}. If one of these holds a column we asked for,
          renaming it to the heading we read lets us use it.
        </Typography>
      </Collapse>
    </Stack>
  );
};

const Suggestions = ({
  check,
  openSuggestions,
}: {
  readonly check: CensusCheckResponse;
  readonly openSuggestions: boolean;
}): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(openSuggestions);
  const { download, isDownloading } = useCensusTemplateDownload();

  return (
    <Stack gap={1}>
      <StatusRow
        chipLabel={plural(check.suggestions.length, "suggestion")}
        chipColor="attention"
        summary={`We read ${plural(check.employees, "employee")}.`}
        action={
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setIsOpen((open) => !open)}
            data-testid="census-upload-check-toggle"
          >
            {isOpen ? "Hide suggestions" : "Show suggestions"}
          </Button>
        }
      />
      <Collapse in={isOpen}>
        <Stack gap={1.5} data-testid="census-upload-check-suggestions">
          <Typography variant="body-medium" color="textSecondary">
            These changes would let carriers quote from your file without coming
            back to you:
          </Typography>
          {check.suggestions.map((suggestion) => (
            <SuggestionItem
              key={suggestionKey(suggestion)}
              suggestion={describeSuggestion(suggestion)}
            />
          ))}
          {check.unmappedHeaders.length > 0 && (
            <UnrecognizedHeadings headings={check.unmappedHeaders} />
          )}
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              variant="tertiary"
              size="sm"
              startIcon={<Icon name="download" size="sm" />}
              loading={isDownloading}
              onClick={download}
              data-testid="census-upload-check-template"
            >
              Download our census template
            </Button>
            <Typography variant="caption" color="textSecondary">
              Suggestions never block submitting your RFP.
            </Typography>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};

const Checked = ({
  check,
  openSuggestions,
}: {
  readonly check: CensusCheckResponse;
  readonly openSuggestions: boolean;
}): React.JSX.Element => {
  if (!readSucceeded(check.outcome)) {
    return (
      <StatusRow
        chipLabel="Couldn't read"
        chipColor="danger"
        summary={`We couldn't read this file: ${unreadableReason(check.outcome)}. Upload a copy we can open to have it checked.`}
      />
    );
  }

  // With no coverage chosen there was nothing to measure the file against, so
  // no suggestions does not mean it is complete.
  if (check.lines.length === 0) {
    return (
      <StatusRow
        chipLabel="Not checked yet"
        chipColor="default"
        summary={`We read ${plural(check.employees, "employee")}. We'll check the file once the RFP has coverages to quote.`}
      />
    );
  }

  if (check.suggestions.length === 0) {
    return (
      <StatusRow
        chipLabel="Looks good"
        chipColor="success"
        summary={`We read ${plural(check.employees, "employee")} and found everything we need.`}
      />
    );
  }

  return <Suggestions check={check} openSuggestions={openSuggestions} />;
};

const renderState = (
  state: CensusUploadCheckState,
  openSuggestions: boolean,
): React.JSX.Element => {
  switch (state.kind) {
    case "checking":
      return (
        <StatusRow
          chipLabel="Checking…"
          chipColor="info"
          summary="We're reading your census."
        />
      );
    case "failed":
      return (
        <Typography variant="caption" color="textSecondary">
          {"We couldn't check this file right now."}
        </Typography>
      );
    case "checked":
      return <Checked check={state.check} openSuggestions={openSuggestions} />;
  }
};

/**
 * What bp-api makes of one census file a broker uploaded: a one-line status
 * that opens into the changes that would let us read all of it. It never
 * blocks the step.
 */
export const CensusUploadCheck = ({
  rfpId,
  documentId,
  openSuggestions = false,
}: CensusUploadCheckProps): React.JSX.Element => {
  const state = useCensusUploadCheck(rfpId, documentId);

  return (
    <Box data-testid="census-upload-check" sx={{ paddingY: 1 }}>
      {renderState(state, openSuggestions)}
    </Box>
  );
};
